import { noop } from 'lodash';
import { useCallback } from 'react';

import {
   MedicalServicePerformableByPractitionerSimple,
   MedicalServicePerformableByPractitionerSimpleQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import {
   Autosuggest,
   Text,
   TextSeparator,
   TextSeparatorType,
   useInteractiveTableContext,
} from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { visitSettlementTranslationKeys } from '../../translations';
import { VisitSettlementRegister } from '../../visitSettlement.types';
import { PriceText } from '../price/PriceText';

import { VisitMedicalServiceTableBean } from './medicalServices.types';

const t = visitSettlementTranslationKeys.shared.medicalServices.selector;

const createMedicalServiceSearchCreator = (practitionerId: number) => {
   return (searchText: string | null) => {
      return async (): CqrsQueryPerformResult<MedicalServicePerformableByPractitionerSimple[]> =>
         performCqrsQuerySearch<MedicalServicePerformableByPractitionerSimple>(() => {
            const query = new MedicalServicePerformableByPractitionerSimpleQuery();
            query.practitionerId = practitionerId;
            if (searchText) {
               query.name = TextPattern.anywhereIgnoreCase(searchText);
            }

            return query;
         });
   };
};

const labelExtractor = (): string => {
   // we do not display anything after selection
   return '';
};

const itemRenderer = (item: MedicalServicePerformableByPractitionerSimple): JSX.Element => {
   return (
      <TextSeparator separator={TextSeparatorType.SPACE}>
         <Text testId={'name'}>{item.name}</Text>
         <PriceText testId={'price'} value={item.price.asNumber()} stripDecimalZeroes />
      </TextSeparator>
   );
};

type MedicalServiceTableSelectorProps = {
   readonly register: VisitSettlementRegister;
   readonly practitionerId: number;
   readonly editable: boolean;
};

export const MedicalServiceTableSelector = (props: MedicalServiceTableSelectorProps): JSX.Element => {
   const { addRow, getCellValue, setCellValue, getValues } = useInteractiveTableContext<VisitMedicalServiceTableBean>();

   const handleSelectionChange = useCallback(
      (selectedMedicalService: MedicalServicePerformableByPractitionerSimple | null): void => {
         if (!selectedMedicalService) {
            return noop();
         }

         return addRow(
            {
               medicalService: {
                  id: selectedMedicalService.medicalServiceId,
                  name: selectedMedicalService.name,
                  priceListPrice: selectedMedicalService.price.asNumber(),
               },
               price: selectedMedicalService.price.asNumber(),
            },
            'prepend'
         );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [getValues, getCellValue, setCellValue]
   );

   return (
      <Autosuggest<MedicalServicePerformableByPractitionerSimple>
         placeholder={t.placeholder}
         controller={props.register('visitSettlement.medicalServicesSearch')}
         searchCreator={createMedicalServiceSearchCreator(props.practitionerId)}
         itemRenderer={itemRenderer}
         onSelectionChange={handleSelectionChange}
         noResultsMessage={t.noResultsMessage}
         labelExtractor={labelExtractor}
         keyProperty={'medicalServiceId'}
         minChars={1}
         testId={'medicalServiceSelector'}
         disabled={!props.editable}
         resetAfterSelection
      />
   );
};
